import React from 'react';
import { Book, HelpCircle, Users, Clock, DollarSign, Calendar } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function FAQ() {
  const faqCategories = [
    {
      id: 'contrats',
      title: 'Contrats',
      icon: <Users className="h-5 w-5" />,
      questions: [
        {
          q: "Comment créer un contrat ?",
          a: "Un parent peut créer un contrat depuis la page Contrats. Renseignez les informations de l'enfant, le taux horaire, les jours et horaires de garde. Un code unique à 6 caractères sera généré pour lier la nounou."
        },
        {
          q: "Comment lier un contrat existant ?",
          a: "Si le contrat a été créé par l'autre partie, allez dans Contrats > Rejoindre par code, entrez le code à 6 caractères, vérifiez les informations et confirmez."
        },
        {
          q: "Puis-je avoir plusieurs contrats ?",
          a: "Oui ! Les parents peuvent créer plusieurs contrats (un par enfant). Les nounous peuvent être liées à plusieurs parents."
        }
      ]
    },
    {
      id: 'pointage',
      title: 'Pointage des Heures',
      icon: <Clock className="h-5 w-5" />,
      questions: [
        {
          q: "Comment pointer les heures (nounou) ?",
          a: "Allez dans Pointage, sélectionnez le contrat, cliquez sur 'Démarrer' au début de la garde et 'Arrêter' à la fin. Les heures sont enregistrées automatiquement."
        },
        {
          q: "Comment déclarer une absence ?",
          a: "Cliquez sur 'Déclarer une absence', choisissez le type (congés payés, enfant malade, etc.), indiquez les dates et ajoutez une note si nécessaire."
        },
        {
          q: "Quand valider les heures (parent) ?",
          a: "Validez les heures avant le 28 du mois dans Validation des Heures. Vérifiez les pointages et validez-les individuellement ou en bloc."
        }
      ]
    },
    {
      id: 'paie',
      title: 'Calcul de la Paie',
      icon: <DollarSign className="h-5 w-5" />,
      questions: [
        {
          q: "Comment calculer la paie ?",
          a: "Nannyou calcule automatiquement : salaire brut (heures × taux), indemnités, cotisations sociales (selon barèmes Urssaf) et salaire net à verser."
        },
        {
          q: "Comment générer un bulletin de salaire ?",
          a: "Dans Paie, sélectionnez le mois, vérifiez les montants et cliquez sur 'Générer le bulletin'. Un PDF est créé automatiquement."
        },
        {
          q: "Comment déclarer à Pajemploi ?",
          a: "Pour les parents Premium : configurez votre numéro Pajemploi dans Profil, puis cliquez sur 'Déclarer à Pajemploi'. La déclaration est envoyée directement."
        }
      ]
    },
    {
      id: 'conges',
      title: 'Gestion des Congés',
      icon: <Calendar className="h-5 w-5" />,
      questions: [
        {
          q: "Comment acquiert-on des congés ?",
          a: "Les assistantes maternelles acquièrent 2,5 jours ouvrables par mois travaillé, soit 30 jours (5 semaines) par an du 1er juin au 31 mai."
        },
        {
          q: "Comment demander des congés (nounou) ?",
          a: "Dans Congés, cliquez sur 'Nouvelle demande', sélectionnez les dates, choisissez le type et ajoutez une note. Le parent sera notifié."
        },
        {
          q: "Comment valider une demande (parent) ?",
          a: "Vous recevez une notification. Allez dans Congés > Demandes en attente, examinez et acceptez ou refusez avec un motif."
        }
      ]
    },
    {
      id: 'premium',
      title: 'Abonnement Premium',
      icon: <HelpCircle className="h-5 w-5" />,
      questions: [
        {
          q: "Quelles sont les différences Gratuit vs Premium ?",
          a: "Gratuit : 1 contrat max, historique 1 mois. Premium (9,90€/mois) : contrats illimités, historique complet, calcul automatique paie, déclaration Pajemploi/Urssaf, support prioritaire."
        },
        {
          q: "Qui peut souscrire Premium ?",
          a: "Parents uniquement. L'abonnement s'applique à tous leurs contrats. Si vous êtes nounou liée à un parent Premium, vous bénéficiez des fonctionnalités."
        },
        {
          q: "Comment souscrire ?",
          a: "Allez dans Mon Compte > Abonnement > Passer à Premium. Renseignez vos informations de paiement. L'accès est activé immédiatement."
        }
      ]
    }
  ];

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Book className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">Foire Aux Questions</h1>
        </div>
        <p className="text-muted-foreground">
          Trouvez rapidement des réponses à vos questions sur Nannyou.
          Pour toute question supplémentaire, contactez <a href="mailto:contact@nannyou.fr" className="text-primary underline">contact@nannyou.fr</a>
        </p>
      </div>

      <div className="space-y-6">
        {faqCategories.map((category) => (
          <Card key={category.id} data-testid={`faq-category-${category.id}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {category.icon}
                {category.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {category.questions.map((item, index) => (
                  <AccordionItem key={index} value={`${category.id}-${index}`}>
                    <AccordionTrigger className="text-left">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="mt-8 bg-primary/5">
        <CardHeader>
          <CardTitle>Besoin d'aide supplémentaire ?</CardTitle>
          <CardDescription>
            Vous pouvez également :
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <p>• Utiliser l'assistant IA (bouton en bas à droite) pour des questions rapides</p>
          <p>• Créer un ticket de support dans la section <a href="/support" className="text-primary underline">Support</a></p>
          <p>• Nous écrire à <a href="mailto:contact@nannyou.fr" className="text-primary underline">contact@nannyou.fr</a></p>
        </CardContent>
      </Card>
    </div>
  );
}
